import { useRef, useEffect } from 'react'
import * as d3 from 'd3'
import { sankey, sankeyJustify, sankeyLinkHorizontal } from 'd3-sankey'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import {
  GROUP_VALUES,
  GROUP_COLORS,
  GROUP_TITLES,
  VALUE_LABELS,
} from './cohorts'

const HEIGHT = 200
const HEADER_ROOM = 18
const DIMENSIONS = Object.keys(GROUP_VALUES)

const valueOf = (nodeId) => nodeId.split(':')[1]
const labelOf = (nodeId) => VALUE_LABELS[valueOf(nodeId)] || valueOf(nodeId)

// The cohorts are the full user_type × account_age × usage_tier cross
// product, so the joint distribution is known — the sankey shows it as
// ribbons flowing User Type → Account Age → Usage Tier, aggregated per
// adjacent column pair.
const buildGraph = (cohorts) => {
  const nodeIds = new Set()
  const linkMap = new Map()

  const addLink = (source, target, value) => {
    const key = `${source}→${target}`
    const existing = linkMap.get(key)
    if (existing) {
      existing.value += value
    } else {
      linkMap.set(key, { source, target, value })
    }
  }

  cohorts.forEach((cohort) => {
    if (!cohort.count) return
    const [ut, aa, tier] = DIMENSIONS.map(
      (dimension) => `${dimension}:${cohort[dimension]}`
    )
    nodeIds.add(ut)
    nodeIds.add(aa)
    nodeIds.add(tier)
    addLink(ut, aa, cohort.count)
    addLink(aa, tier, cohort.count)
  })

  return {
    nodes: [...nodeIds].map((id) => ({ id })),
    links: [...linkMap.values()],
  }
}

const CohortSankey = ({ cohorts }) => {
  const containerRef = useRef(null)
  const tooltipRef = useRef(null)
  const theme = useTheme()

  const total = cohorts
    ? cohorts.reduce((sum, cohort) => sum + cohort.count, 0)
    : 0

  useEffect(() => {
    if (!cohorts || total === 0) return undefined

    const textColor = theme.palette.text.secondary
    const width = containerRef.current.offsetWidth
    const graph = buildGraph(cohorts)

    const layout = sankey()
      .nodeId((d) => d.id)
      .nodeAlign(sankeyJustify)
      .nodeWidth(8)
      .nodePadding(12)
      .extent([
        [0, HEADER_ROOM],
        [width, HEIGHT - 4],
      ])

    const { nodes, links } = layout({
      nodes: graph.nodes.map((d) => ({ ...d })),
      links: graph.links.map((d) => ({ ...d })),
    })

    const svg = d3
      .select(containerRef.current)
      .append('svg')
      .attr('width', width)
      .attr('height', HEIGHT)

    // Column headers, anchored to each column's node x positions.
    const columnX = DIMENSIONS.map((dimension) => {
      const node = nodes.find((d) => d.id.startsWith(`${dimension}:`))
      return node ? (node.x0 + node.x1) / 2 : null
    })
    DIMENSIONS.forEach((dimension, index) => {
      if (columnX[index] === null) return
      svg
        .append('text')
        .attr('x', columnX[index])
        .attr('y', 10)
        .attr(
          'text-anchor',
          index === 0
            ? 'start'
            : index === DIMENSIONS.length - 1
            ? 'end'
            : 'middle'
        )
        .style('fill', textColor)
        .style('font-size', '9px')
        .style('letter-spacing', '0.5px')
        .style('text-transform', 'uppercase')
        .text(GROUP_TITLES[dimension])
    })

    // One gradient per ribbon so it fades source color → target color.
    const defs = svg.append('defs')
    links.forEach((link, index) => {
      const gradient = defs
        .append('linearGradient')
        .attr('id', `heartbeats-sankey-grad-${index}`)
        .attr('gradientUnits', 'userSpaceOnUse')
        .attr('x1', link.source.x1)
        .attr('x2', link.target.x0)
      gradient
        .append('stop')
        .attr('offset', '0%')
        .attr('stop-color', GROUP_COLORS[valueOf(link.source.id)])
      gradient
        .append('stop')
        .attr('offset', '100%')
        .attr('stop-color', GROUP_COLORS[valueOf(link.target.id)])
    })

    const tooltip = tooltipRef.current
    const showTooltip = (text) => {
      tooltip.textContent = text
      tooltip.style.opacity = 1
    }
    const moveTooltip = (event) => {
      // Pointer coords come back in visual (post-transform) pixels;
      // divide by the wallboard scale (visual width over layout width)
      // to get back to local coordinates.
      const node = containerRef.current
      const scale = node.getBoundingClientRect().width / node.offsetWidth || 1
      const [rawX, rawY] = d3.pointer(event, node)
      const x = rawX / scale
      const y = rawY / scale
      // Flip to the left of the cursor near the panel's right edge so
      // the tooltip stays inside the narrow sidebar.
      const flip = x > width * 0.55
      tooltip.style.left = flip ? 'auto' : `${x + 12}px`
      tooltip.style.right = flip ? `${width - x + 12}px` : 'auto'
      tooltip.style.top = `${y - 24}px`
    }
    const hideTooltip = () => {
      tooltip.style.opacity = 0
    }

    svg
      .append('g')
      .selectAll('path')
      .data(links)
      .join('path')
      .attr('d', sankeyLinkHorizontal())
      .attr('fill', 'none')
      .attr('stroke', (d, i) => `url(#heartbeats-sankey-grad-${i})`)
      .attr('stroke-width', (d) => Math.max(1, d.width))
      .attr('stroke-opacity', 0.4)
      .on('mouseenter', function highlight(event, d) {
        d3.select(this).attr('stroke-opacity', 0.7)
        showTooltip(
          `${labelOf(d.source.id)} → ${labelOf(d.target.id)} · ${d.value}`
        )
      })
      .on('mousemove', moveTooltip)
      .on('mouseleave', function unhighlight() {
        d3.select(this).attr('stroke-opacity', 0.4)
        hideTooltip()
      })

    // Compact: no node labels — the colors match the counts list below
    // the diagram, and tooltips carry the exact numbers.
    svg
      .append('g')
      .selectAll('rect')
      .data(nodes)
      .join('rect')
      .attr('x', (d) => d.x0)
      .attr('y', (d) => d.y0)
      .attr('width', (d) => d.x1 - d.x0)
      .attr('height', (d) => Math.max(1, d.y1 - d.y0))
      .attr('rx', 2)
      .attr('fill', (d) => GROUP_COLORS[valueOf(d.id)])
      .on('mouseenter', (event, d) => {
        showTooltip(`${labelOf(d.id)} · ${d.value}`)
      })
      .on('mousemove', moveTooltip)
      .on('mouseleave', hideTooltip)

    const container = containerRef.current
    return function cleanup() {
      container.innerHTML = ''
    }
  }, [cohorts, total, theme])

  if (total === 0) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ py: 6, textAlign: 'center' }}
      >
        No mics on right now.
      </Typography>
    )
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Box ref={containerRef} sx={{ width: '100%', height: HEIGHT }} />
      <Box
        ref={tooltipRef}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          opacity: 0,
          pointerEvents: 'none',
          transition: 'opacity 0.15s',
          bgcolor: 'rgba(0, 0, 0, 0.8)',
          color: 'white',
          px: 1.25,
          py: 0.5,
          borderRadius: 1,
          fontSize: 12,
          whiteSpace: 'nowrap',
          zIndex: 1,
        }}
      />
    </Box>
  )
}

export default CohortSankey
